/* eslint-disable react/require-default-props */
/**
 * AppAccessTiles component
 *
 * This component displays a list of apps with their corresponding entitlements.
 * It is used to display the apps that will be granted access to the role.
 */

import React from 'react';
import { ApplicationIcon, Box, Typography } from '@saviynt/design-system';
import cx from 'classnames';

import { useInvertedLocalize } from '../../../utils/localization';

import invertedMessages from './messages';

import './AppAccessTiles.css';

interface AppAccess {
  name: string;
  numEntitlements: number;
  applicationIcon: string;
}

interface AppAccessTilesProps {
  title: string;
  apps: AppAccess[]; // Array of apps to display
  className?: string;
}

/**
 * Component for displaying application access tiles showing which apps will be granted access
 *
 * @param props - The component props
 * @returns JSX element containing the app access tiles
 */
function AppAccessTiles({ title, apps, className }: AppAccessTilesProps) {
  const l = useInvertedLocalize(invertedMessages);

  return (
    <Box tag='section' className={cx('Roles-AppAccessTiles', className)}>
      <Box className='Roles-AppAccessTiles-header'>
        <Typography kind='h3' className='Roles-AppAccessTiles-title'>
          {title}
        </Typography>
      </Box>

      <Box className='Roles-AppAccessTiles-grid'>
        {apps.map((app) => (
          <Box key={app.name} className='Roles-AppAccessTiles-tile'>
            <Box className='Roles-AppAccessTiles-iconContainer'>
              <ApplicationIcon
                logo={app.applicationIcon}
                size='medium'
                className='Roles-AppAccessTiles-icon'
              />
            </Box>

            {/* App info */}
            <Box className='Roles-AppAccessTiles-info'>
              <Typography
                kind='body2'
                color='--color-foreground-neutral-bold'
                lineLimit={1}
                tooltipConfigs={{
                  text: app.name,
                  placement: 'top',
                  maxWidth: '10rem',
                }}
                className='Roles-AppAccessTiles-appName'>
                {app.name}
              </Typography>
              <Typography
                kind='body3'
                className='Roles-AppAccessTiles-entitlements'>
                {l(
                  '{count, plural, one {# entitlement} other {# entitlements}}',
                  { count: app.numEntitlements }
                )}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

export default AppAccessTiles;
